/**
 * Phone Number Safeguards
 * 
 * Prevents automatic assignment of random or invalid phone numbers.
 * Ensures strict validation and sanitization of phone number data.
 */

// Common test/dummy phone numbers that should be rejected
const FORBIDDEN_PHONE_NUMBERS = [
  '+15555555555',
  '+1234567890',
  '+1123456789',
  '+11234567890',
  '+15551234567',
  '+10123456789',
  '1234567890',
  '5555555555',
  '123456789',
  '0123456789',
];

// Patterns that indicate test or invalid phone numbers
const FORBIDDEN_PATTERNS = [
  /^(\+1)?555555\d{4}$/, // 555-555-XXXX pattern
  /^(\+1)?123456\d{4}$/, // 123-456-XXXX pattern
  /^(\+1)?000000\d{4}$/, // 000-000-XXXX pattern
  /^(\+1)?111111\d{4}$/, // 111-111-XXXX pattern
  /^(\+1)?\d{3}\1{7}$/,   // Repeated digit patterns (e.g., 555-555-5555)
  /^(\+1)?0{10}$/,        // All zeros
  /^(\+1)?1{10}$/,        // All ones
];

/**
 * Validates that a phone number is legitimate and not auto-generated
 * @param phoneNumber - The phone number to validate
 * @returns Object with isValid boolean and optional error message
 */
export function validatePhoneNumberSafety(phoneNumber: string | null | undefined): {
  isValid: boolean;
  error?: string;
  sanitizedValue: string | null;
} {
  // Allow null/undefined/empty phone numbers (user choice not to provide one)
  if (!phoneNumber || typeof phoneNumber !== 'string' || phoneNumber.trim() === '') {
    return {
      isValid: true,
      sanitizedValue: null,
    };
  }

  const trimmed = phoneNumber.trim();

  // Check against forbidden exact matches
  if (FORBIDDEN_PHONE_NUMBERS.includes(trimmed)) {
    return {
      isValid: false,
      error: 'This appears to be a test or placeholder phone number. Please enter your actual phone number.',
      sanitizedValue: null,
    };
  }

  // Check against forbidden patterns
  for (const pattern of FORBIDDEN_PATTERNS) {
    if (pattern.test(trimmed)) {
      return {
        isValid: false,
        error: 'This phone number appears to be invalid or a placeholder. Please enter a valid phone number.',
        sanitizedValue: null,
      };
    }
  }

  // Validate basic phone number format
  const digitsOnly = trimmed.replace(/\D/g, '');
  const withoutCountryCode = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;

  if (withoutCountryCode.length !== 10) {
    return {
      isValid: false,
      error: 'Please enter a valid 10-digit phone number.',
      sanitizedValue: null,
    };
  }

  // Check for sequential numbers (e.g., 1234567890)
  let sequential = true;
  for (let i = 1; i < withoutCountryCode.length; i++) {
    if (parseInt(withoutCountryCode[i]) !== parseInt(withoutCountryCode[i - 1]) + 1) {
      sequential = false;
      break;
    }
  }

  if (sequential) {
    return {
      isValid: false,
      error: 'Please enter a valid phone number, not a sequential number.',
      sanitizedValue: null,
    };
  }

  // Format to E.164 format
  const sanitizedValue = `+1${withoutCountryCode}`;

  return {
    isValid: true,
    sanitizedValue,
  };
}

/**
 * Sanitizes phone number input for database storage
 * Always returns null for invalid or suspicious phone numbers
 * @param phoneNumber - Raw phone number input
 * @returns Sanitized phone number or null
 */
export function sanitizePhoneNumberForStorage(phoneNumber: string | null | undefined): string | null {
  const validation = validatePhoneNumberSafety(phoneNumber);
  
  if (!validation.isValid) {
    console.warn(`[PhoneSafeguards] Rejected potentially invalid phone number: ${phoneNumber} - ${validation.error}`);
    return null;
  }

  return validation.sanitizedValue;
}

/**
 * Logs phone number operations for monitoring and debugging
 * @param operation - The operation being performed
 * @param input - Original input
 * @param output - Processed output
 * @param context - Additional context
 */
export function logPhoneNumberOperation(
  operation: 'profile_create' | 'profile_update' | 'address_create' | 'address_update',
  input: string | null | undefined,
  output: string | null,
  context: { userId?: string; source?: string }
) {
  console.log(`[PhoneSafeguards] ${operation}:`, {
    input: input ? `${input.substring(0, 3)}...${input.substring(input.length - 4)}` : null,
    output: output ? `${output.substring(0, 3)}...${output.substring(output.length - 4)}` : null,
    isAutoAssigned: false, // We never auto-assign
    context,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Profile update safeguard - ensures no automatic phone number assignment
 * @param updateData - Profile update data
 * @param userId - User ID for logging
 * @returns Sanitized update data
 */
export function safeguardProfilePhoneNumber(
  updateData: any,
  userId: string,
  source: string = 'unknown'
): any {
  if ('phone_number' in updateData) {
    const originalValue = updateData.phone_number;
    const sanitizedValue = sanitizePhoneNumberForStorage(originalValue);
    
    logPhoneNumberOperation('profile_update', originalValue, sanitizedValue, {
      userId,
      source,
    });

    return {
      ...updateData,
      phone_number: sanitizedValue,
    };
  }

  return updateData;
}